import _, {shuffle} from "lodash";
import {app, idba, onair, print, throwError, use_mp3} from "./config.ts";
import {Api} from "./api.ts";
import {Utils} from "./utils.ts";
import {OnairPlayer} from "./onair.player.ts";
import {EventBus} from "./event.bus.ts";
import {IDBTables} from "./idb.service.ts";
import type {DaoApiSchedule} from "../daos/dao-api-schedule.ts";
import type {ApiResponse, IDBData} from "./interfaces.ts";
import type {DaoMedia} from "../daos/dao-media.ts";
import type {DaoApiConnection} from "../daos/dao-api-connection.ts";
import type {DaoApiChannelData} from "../daos/dao-api-channel-data.ts";
import {audioContext} from "../main.tsx";
import type {DaoApiMediaResource} from "../daos/dao-api-media-resource.ts";

export interface OnairOptions {
  volume: number,
  mute: boolean,
  channel?: string | undefined,
}

export const OnairEvent = {
  SCHEDULE: "schedule",
  MEDIA: "media",
  MEDIA_START: "media_start",
  MEDIA_END: "media_end",
  MEDIA_ERROR: "media_error",
  DURATION: "duration",
  ERROR: "error",
}

export type OnairType = 'EM' | 'TTS' | 'AD' | 'CM' | 'RQ' | 'PK' | 'UD' | 'GN' | 'UNK';

export class Onair extends EventBus {
  idata: IDBData | undefined;
  options: OnairOptions | undefined;
  ticker: number = NaN;
  tickerGap: number = 1000;
  millisecond: number = 0;

  private active: number = 0;
  private players: OnairPlayer[] = [];

  schedules: {[key: number]: DaoMedia[]} = {};
  advertises: DaoMedia[] = [];
  media: DaoMedia | null = null;

  schedules_next: {[key: number]: DaoMedia[]} | null = null;
  advertises_next: DaoMedia[] = [];

  constructor() {
    super();
  }

  async init(millisecond: number): Promise<Onair> {
    this.uninit();

    this.setMillisecond(millisecond);

    // Onair 초기화
    this.idata = await idba.getByField<IDBData>(IDBTables.configs, 'key', 'onair');
    if(this.idata === undefined) {
      this.idata = {key: 'onair', value: Utils.toJson({volume: 1, mute: false}, true)} as IDBData;
      await this.save(this.idata);
    }

    this.options = Utils.fromJson(this.idata.value as string, undefined, true) as OnairOptions;
    if(!_.has(this.options, 'volume')) this.options.volume = 1;
    if(!_.has(this.options, 'mute')) this.options.mute = false;

    // OnairPlayer 2개 생성
    this.players.push(
      new OnairPlayer(
        document.getElementById('web-player-audio01') as HTMLAudioElement,
        this.options
      )
    );
    this.players.push(
      new OnairPlayer(
        document.getElementById('web-player-audio02') as HTMLAudioElement,
        this.options
      )
    );

    this.tickerStart();
    return this;
  }

  uninit() {
    for(let i: number = 1; i <= this.players.length; i++) {
      const player = this.getPlayer(i);
      if(player) {
        player.uninit().then(() => {});
      }
    }

    this.tickerStop();
  }

  tickerStart() {
    // 네이티브(백그라운드/포그라운드 전환)에서 반복 호출되므로
    // 기존 인터벌을 먼저 정리하지 않으면 전환할 때마다 티커가 누적된다.
    this.tickerStop();
    this.ticker = setInterval(
      () => this.tickerHandler(),
      this.tickerGap
    );
  }

  tickerStop() {
    if(!_.isNaN(this.ticker)) {
      clearInterval(this.ticker);
    }
    this.ticker = NaN;
  }

  async save(data: IDBData): Promise<void> {
    if(!_.has(data, '_id')) {
      data._id = await idba.add<IDBData>(IDBTables.configs, data) as string;
    } else {
      await idba.save<IDBData>(IDBTables.configs, data);
    }
  }

  saveOptions() {
    if(this.idata !== undefined && this.options !== undefined) {
      this.idata.value = Utils.toJson(this.options, true);
      this.save(this.idata).then(() => {
      });
    }
  }

  async clear() {
    return new Promise<void>((resolve) => {

      for(const player of this.players) {
        player.stop();
      }

      this.active = -1;
      this.media = null;
      this.advertises = [];
      this.schedules = {};
      this.schedules_next = null;

      setTimeout(() => resolve(), 500);
    });
  }

  tickerHandler() {
    try {
      const previous: Date = Utils.now(this.millisecond);
      this.setMillisecond(Utils.milliseconds());
      const date: Date = Utils.now(this.millisecond);

      const days: number = date.getDate();
      const hours: number = date.getHours();
      const minutes: number = date.getMinutes();
      const seconds: number = date.getSeconds();

      if(!_.isEqual(previous.getDate(), days)) {
        print("Date Changes");

        if(this.schedules_next !== null) {
          for(const schedulePriority in this.schedules_next) {
            this.setSchedule(Number(schedulePriority), this.schedules_next[schedulePriority]);
          }
          this.advertises = this.advertises_next;

          this.schedules_next = null;
          this.advertises_next = [];
        }
      }

      if(!_.isEqual(previous.getHours(), hours)) {
        // print("Hours Changes");
      }

      if(!_.isEqual(previous.getMinutes(), minutes)) {

        // 매시간마다 네트워크 체크(백업시간과 같을때)
        if(_.isEqual(app.backup, minutes)) {
          window.wpi?.network_check?.('network_status', app.user?.server as string ?? null);
        }

        // print("Minutes Changes", Utils.toDateString(date, 'yyyy-MM-dd HH:mm'));

        // 스케줄 정보 업데이트 23:30 이후부터
        if(app.isLogin()) {
          if(hours === 23 && minutes > 30 && this.schedules_next === null) {
            this.schedules_next = {};
            // 익일 스케줄 인출
            const tomorrow = new Date(date);
            tomorrow.setDate(days + 1);
            this.schedule(Utils.toDateString(tomorrow, 'yyyy-MM-dd')).then(() => {
              print("익일 스케줄 정보 인출", tomorrow, this.schedules_next, this.advertises_next, this.schedules);
            });
          }
        }
      }

      if(!_.isEqual(previous.getSeconds(), seconds)) {
        // print("Seconds Changes", seconds, app.backup);
        // 접속정보 갱신

        if(app.connect && _.isEqual(app.backup, seconds)) {
          if(app.online) {
            Api.connection()
              .then((_rs: ApiResponse<DaoApiConnection>) => {
              });
          }
        }

        // 현재 음원
        const media: DaoMedia | null = this.getMedia()
        if(!_.isNull(media) && !_.isEqual(media, this.media)) {

          this.setMedia(media)
            .then(() => {
            });
        }

        return this.media;
      }
    } catch (e) {
    }
  }

  async setMedia(media: DaoMedia|null) {
    try {
      console.log(this.schedules[1] ??  'empty emergency');
      const previousPlayer: OnairPlayer | null = this.getPlayer(this.active);
      if(previousPlayer) {
        await previousPlayer.stop();
      }

      const newPlayerIndex: number = this.active === 1 ? 0 : 1;
      // const newPlayerIndex: number = 0;
      const nextPlayer: OnairPlayer | null = this.getPlayer(newPlayerIndex);
      if(nextPlayer) {
        this.active = newPlayerIndex;
        this.media = media;

        onair.emit(OnairEvent.MEDIA, media);
        if(media === null) return;

        const m4a: string|undefined = app.user?.use_m4a ?? undefined;
        if(m4a) media.m4a = m4a;

        // 시작 시간 기록
        media.startAt = Utils.toDateString();
        // 음원 재생
        nextPlayer.start(media).then(async () => {
          // 다음 음원 준비
          await this.prepareMedia(m4a ?? 'mp3');
          // 정상음원인 경우 로그 송출
          Api.logs(media).then(() => {});
        }).catch((err) => {
          console.error("nextPlayer.start", err);
          console.error(err?.stack);
        });
      }
    } catch (e) {
      void e;
    }
  }

  getMedia(current: Date|null = null): DaoMedia | null {
    // const is_search: boolean = current !== null;
    if(!current) current = this.getDate();

    let matches: DaoMedia | null = null;
    if(this.schedules) {
      for(const priority in this.schedules) {
        const scheduleMedias: DaoMedia[] = this.schedules[priority];
        if(scheduleMedias.length > 0) {
          let passed = NaN;

          for(let i = 0; i < scheduleMedias.length; i++) {
            const media: DaoMedia = scheduleMedias[i];

            const start_format = media.start.length === 19 ? 'yyyy-MM-dd HH:mm:ss' : 'HH:mm:ss';
            const start = Utils.toDate(media.start, start_format, current);
            const end_format = media.end.length === 19 ? 'yyyy-MM-dd HH:mm:ss' : 'HH:mm:ss';
            const end = Utils.toDate(media.end, end_format, current);

            if(this.isBefore(start)) {
              if(this.isRemovable(media.type))
                break;
            }

            if(this.isPassed(end)) {
              if(!this.isRemovable(media.type)) {
                if(media.gap) {
                  const next_gap: number = media.gap * 1000;

                  const next_start: Date = Utils.now(start.getTime() + next_gap);
                  const next_end: Date = Utils.now(end.getTime() + next_gap);

                  media.start = Utils.toDateString(next_start, start_format);
                  media.start_base = media.start;
                  media.end = Utils.toDateString(next_end, end_format);
                }
              } else passed = i;
            } else {
              if(this.isCurrent(start, end, current)) {
                matches = media;
                break;
              }
            }
          }
          if(!_.isNaN(passed) && this.isRemovablePriority(Number(priority))) scheduleMedias.splice(0, (passed + 1));
          // priority 우선의 데이터를 찾으면 빠져나옴
          if(matches != null) break;
        } else {
          this.scheduleDelete(Number(priority));
        }
      }
    }
    return matches;
  }

  getNextMedia(currentMedia: DaoMedia|null): DaoMedia|null {
    let next: DaoMedia|null = null;
    if(currentMedia) {
      const datetime: Date = Utils.toDate(currentMedia.end, (currentMedia.start.length === 19 ? 'yyyy-MM-dd HH:mm:ss' : 'HH:mm:ss'), this.getDate());
      if(this.schedules) {
        for(const priority in this.schedules) {
          const scheduleMedias: DaoMedia[] = this.schedules[priority];
          // 일치하는 데이터 인출
          const finded = scheduleMedias.find((media: DaoMedia) => {
            const start_format
              = media.start.length === 19 ? 'yyyy-MM-dd HH:mm:ss' : 'HH:mm:ss';
            const start = Utils.toDate(media.start, start_format, datetime);
            const end_format
              = media.end.length === 19 ? 'yyyy-MM-dd HH:mm:ss' : 'HH:mm:ss';
            const end = Utils.toDate(media.end, end_format, datetime);
            return this.isCurrent(start, end, datetime);
          });

          if(finded) {
            next = finded;
            break;
          }
        }
      }
    }
    return next;
  }

  /**
   * 다음 음원 준비
   * @param bitrate
   */
  async prepareMedia(bitrate: string) {
    let media: DaoMedia | null = this.media;
    if(!media) media = this.getMedia();

    if(!_.isNull(media)) {
      const next: DaoMedia|null = this.getNextMedia(media);
      if(next !== null) {
        if(bitrate && bitrate !== 'mp3') next.m4a = bitrate;

        let next_media_file: string = this.getMediaFilename(next.media, next.m4a ?? null, (next.m4a && !use_mp3) ? 'm4a' : 'mp3');
        // 인터페이스 통신에 의한 저장된 음원 URL 인출
        const next_media_url = await window.wpi?.media_uri?.(next_media_file) as string;
        // console.log("###### next_media_url:", next_media_url, next_media_file);
        // 다음 미디어 URL 존재시
        if(!_.isEmpty(next_media_url)) next.url = next_media_url;
        else {
          const ac = new AbortController();
          const next_rs: ApiResponse<DaoApiMediaResource> | void = await Api.media_standby(next, ac)
            .catch(() => {
            });
          if (next_rs && next_rs.data) {
            const next_resource = next_rs.data as DaoApiMediaResource;
            // 인터페이스 통신에 의한 음원 저장
            next_media_file = this.getMediaFilename(next.media, next_resource.bitrate ?? null, next_resource.file ?? 'mp3');
            await window.wpi?.media_save?.(next_media_file, String(next_resource.length ?? 0), next_resource.url);
          }
        }
      }
    }
  }

  getMediaFilename(media: number, bitrate: string|null = null, ext: string = 'mp3' ) {
    return `${Utils.pad(media, 10)}${(bitrate ? `.${bitrate.toUpperCase()}` : '.128k')}.${ext}`;
  }

  getPriority(type: OnairType) {
    switch(type) {
      case "EM":  return 1;
      case "TTS": return 2;
      case "AD":  return 3;
      case "CM":  return 4;
      case "RQ":  return 5;
      case "PK":  return 6;
      case "UD":  return 7;
      default:
      case "GN":  return 8;
    }
  }

  getPriorityName(type: OnairType) {
    switch(type) {
      case "EM":  return "비상음원";
      case "TTS": return "AI음성 안내방송";
      case "AD":  return "지정안내방송";
      case "CM":  return "안내방송";
      case "RQ":  return "신청곡";
      case "PK":  return "피크타임";
      case "UD":  return "채널";
      default:
      case "GN":  return "전용방송";
    }
  }

  isClearable(type: OnairType): boolean {
    return this.isClearablePriority(
      this.getPriority(type)
    );
  }

  isClearablePriority(priority: number): boolean {
    switch(priority) {
      case 1:
      case 2:
      case 3:
      case 5:
      case 6:
      case 7:     return true;
      default:    return false;
    }
  }

  isRemovable(type: OnairType): boolean {
    return this.isRemovablePriority(
      this.getPriority(type)
    );
  }

  isRemovablePriority(priority: number): boolean {
    switch(priority) {
      case 2:
      case 3:
      case 4:
      case 5:
      case 6:
      case 8:     return true;
      default:    return false;
    }
  }

  getAdvertises(): DaoMedia[] {
    return this.advertises;
  }

  isAdvertise(type: OnairType): boolean {
    switch(type) {
      case "AD":
      case "CM":
      case "TTS": return true;
      default:    return false;
    }
  }

  setMillisecond(milliseconds: number): void {
    this.millisecond = milliseconds;
  }

  getDate(): Date {
    return new Date(this.millisecond);
  }

  isCurrent(start: Date, end: Date, current: Date|undefined = undefined) {
    return (!this.isBefore(start, current) && !this.isPassed(end, current));
  }

  isBefore(date: Date, current: Date|undefined = undefined): boolean {
    if(!current) current = this.getDate();
    return date?.getTime() > current.getTime();
  }

  isPassed(date: Date, current: Date|undefined = undefined): boolean {
    if(!current) current = this.getDate();
    return date?.getTime() <= current.getTime();
  }

  /**
   * 오프라인 상태일 경우 비상음원 재생
   */
  offline(online: boolean = false): void {
    if(online) {
      if(this.schedules)
        this.scheduleDelete(
          this.getPriority('EM')
        );
    }
  }

  /**
   *
   */
  setEmergency(): void {
    console.log("setEmergency");

    try {
      onair.emergency()
        .then((medias: DaoMedia[]|undefined) => {
          console.log(medias, this.schedules);
          if(medias) {
            this.schedules[this.getPriority('EM')] = medias;
          }
        });
    } catch (e) {
      console.log("setEmergency", e);
    }
  }

  /**
   * 비상음원
   */
  async emergency(): Promise<DaoMedia[]|undefined> {
    const limit: number = 100;
    const medias: {[key: number]: DaoMedia} = {};
    if(this.schedules) {
      const countMedias = (): number => {
        return Object.keys(medias).length;
      };

      for(const priority in this.schedules) {
        const _medias: DaoMedia[] = this.schedules[priority];
        if([5, 6, 7, 8].includes(Number(priority))) {
          for(const _media of _medias) {
            const media_no = Number(_media.media);
            if(!(media_no in _media)) {
              let _media_file: string = this.getMediaFilename(_media.media, _media.m4a ?? null, (_media.m4a && !use_mp3) ? 'm4a' : 'mp3');
              // 인터페이스 통신에 의한 저장된 음원 URL 인출
              const media_url = await window.wpi?.media_uri?.(_media_file) as string;
              if(media_url) {
                medias[media_no] = { ..._media };
              }
              if(countMedias() >= limit) break;
            }
          }
        }
        if(countMedias() >= limit) break;
      }

      let __medias: DaoMedia[] = shuffle(Object.values(medias) ?? []) as DaoMedia[];
      // 음원데이터 비상음원으로 재정의
      if(__medias && __medias.length > 0) {

        const __mediasDuration = __medias.reduce((sum, m) => sum + (m.duration ?? 0), 0);

        let start = this.getDate().getTime();
        /// 랜덤 섞기
        for(const __media of __medias) {
          const dao: DaoMedia = __media as DaoMedia;
          dao.type = 'EM';
          dao.priority = this.getPriority(dao.type);
          dao.start = Utils.toDateString(Utils.now(start));
          dao.start_base = dao.start;
          start = start + (dao.duration * 1000)
          dao.end = Utils.toDateString(Utils.now(start));
          dao.startAt = undefined;
          // 반복 시간
          dao.gap = __mediasDuration;
        }
      }

      console.log(__medias, medias);
      return __medias;
    }
  }

  restore(): void {
    if(this.media != null) {
      if(this.isClearablePriority(this.media.priority)) {
        this.scheduleDelete(this.media.priority);

        // 채널종료시 채널 사용 데이터를 리셋 / 디비저장
        if(this.media.type === "UD") {
          if(this.options !== undefined) {
            delete this.options.channel;
            this.saveOptions();
          }

          Api.channel_clear()
            .then(() => {});
        }
      }
    }
  }

  /**
   * 채널 데이터
   * @param no
   */
  async channel(no: string) {
    const rs: ApiResponse<DaoApiChannelData> = await Api.channel_data(no);
    const daoApiChannelData: DaoApiChannelData = rs.data;
    if(daoApiChannelData) {
      // 인터페이스로 환경설정 파일 전달
      try {
        Utils.saveConfig('CHANNEL', daoApiChannelData);
      } catch (e) {}

      _.forEach(daoApiChannelData.medias, (media: DaoMedia): void => {
        media.onair_name = daoApiChannelData.channel.name;
        media.onair_no = daoApiChannelData.channel.no;
      });
    }

    if(this.options !== undefined) {
      this.options.channel = no;
      this.saveOptions();
    }
    onair.setSchedule(onair.getPriority('UD'), daoApiChannelData.medias);
    return daoApiChannelData.medias;
  }

  /**
   * 스케줄 데이터 처리
   * @param date
   */
  async schedule(date: string|null = null) {
    try {
      const current_date = Utils.toDateString(this.getDate(), 'yyyy-MM-dd');
      if(_.isEmpty(date) || _.isNull(date)) date = current_date;

      const isToday: boolean = _.isEqual(current_date, date);
      const rs: ApiResponse<DaoApiSchedule> = await Api.schedule(date);
      if(rs.statusText !== 'OK' || rs.data.rs !== 1) {
        throwError('스케줄 데이터 처리 실패');
      } else {
        const apiSchedule = rs.data as DaoApiSchedule;

        // 인터페이스로 환경설정 파일 전달
        try {
          Utils.saveConfig('SCHEDULE', apiSchedule);
        } catch (e) {}

        const schedules: {[key: number]: DaoMedia[]} = {};
        if(apiSchedule.schedules) {
          _.forEach(apiSchedule.schedules, (medias: DaoMedia[], type: string) => {
            const type_no = Number(type);
            schedules[type_no] = [];
            if(medias.length > 0) {
              _.forEach(medias, (media: DaoMedia) => {
                // 전체 스케줄
                const priority = _.isEmpty(media.priority) ? Number(media.priority) : onair.getPriority(media.type);
                schedules[priority].push(media);

                // 안내방송 복사
                if(this.isAdvertise(media.type)) {
                  if(isToday) {
                    this.advertises.push({...media});
                  } else {
                    this.advertises_next.push({...media});
                  }
                }
              });
            }
          })
        }

        if(isToday) {
          this.schedules = schedules;
        } else {
          this.schedules_next = schedules;
        }
      }
    } catch {
      throwError('스케줄처리중 오류');
    }
  }

  setSchedule(priority: number, medias: DaoMedia[]) {
    this.scheduleDelete(priority);
    this.schedules[priority] = medias;
    this.emit(OnairEvent.SCHEDULE, this.schedules);
  }

  getSchedule(priority: number): DaoMedia[] {
    return this.schedules[priority] ?? undefined;
  }

  scheduleDelete(priority: number): void {
    if(_.has(this.schedules, priority)) {
      delete this.schedules[priority];
    }
  }

  recover(): void {
    const priority = this.getPriority('UD');
    const udMedias:DaoMedia[] = this.getSchedule(priority);

    this.schedule()
      .then(async () => {
        if(udMedias) this.setSchedule(priority, udMedias);

        // 플레이어 정리
        const p0 = this.getPlayer(0)?.recover(new Event("error"));
        const p1 = this.getPlayer(1)?.recover(new Event("error"));
        await Promise.all([p0, p1]);
        // 음원정리
        await this.setMedia(null);
      });
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /// 플레이어 관련
  /**
   *
   * @param volume 0-100
   */
  setVolume(volume: number): Onair {
    if(this.options) {
      this.options.volume = Number(Math.max(Math.min((volume / 100), 1), 0).toFixed(2));
      this.saveOptions();
      this.setPlayers();
    }
    return this;
  }

  getVolume(): number {
    return Math.max(Math.min((this.options?.volume ?? 1) * 100, 100), 0);
  }

  setMute(mute: boolean): Onair {
    if(this.options) {
      this.options.mute = mute;
      this.saveOptions();
      this.setPlayers();
    }
    return this;
  }

  isMute(): boolean {
    return this.options?.mute ?? false;
  }

  get activePlayer(): OnairPlayer | null {
    return this.getPlayer(this.active);
  }

  getPlayer(index: number): OnairPlayer | null {
    return this.players[index] || null;
  }

  setPlayers() {
    if(this.options) {
      for(const player of this.players) {
        player.setOption(this.options);
      }
    }
  }
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  /**
   * 사용자 이벤트 처리
   */
  async awaikeOnair() {
    if(audioContext) {
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }
      const audio: HTMLAudioElement | null = document.getElementById('web-player-awaiker') as HTMLAudioElement;
      if(audio) {
        if (audioContext.state === 'suspended') {
          await audioContext.resume();
        }

        if (audio.paused) {
          await audio.play().catch(e => console.error("Silent audio play failed:", e));
          print("Silent audio started to keep screen awake.");
        }
      }
    }
  }
}